'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Button } from '@/components/Button';
import { Card, StatBlock } from '@/components/Card';
import { Input, Select } from '@/components/Input';
import { NeedsAuth } from '@/components/NeedsAuth';
import { PageTitle } from '@/components/PageTitle';
import { Pill, PillGroup } from '@/components/Pill';
import { getClient } from '@/lib/api';
import { eur, shortDate } from '@/lib/fmt';
import { buildCurve } from '@/lib/projection';
import {
  activeChapterAt,
  cryptoRandomId,
  currentMonthlySalary,
  effectiveStartingBalance,
  monthlyFixedExpenses,
  monthlySpendingSubtotal,
  planFromApi,
  recurringExpensesTotal,
  S_AND_P_ANNUAL_VOLATILITY,
  simulateWithBands,
  type IncomeChapter,
  type Plan,
  type RecurringExpense,
  type StartingBalance,
} from '@/lib/planner';
import { useCombinedBalance } from '@/lib/balance';
import type { components } from '@/lib/api.types';

type Snapshot = components['schemas']['ProjectionOut'];
type Tab = 'trajectory' | 'planner';

export default function TrajectoryPage() {
  return (
    <NeedsAuth>
      <Inner />
    </NeedsAuth>
  );
}

function Inner() {
  const [tab, setTab] = useState<Tab>('planner');

  return (
    <div className="flex flex-col gap-6">
      <PageTitle
        actions={
          <PillGroup>
            <Pill active={tab === 'planner'} onClick={() => setTab('planner')}>
              Planner
            </Pill>
            <Pill active={tab === 'trajectory'} onClick={() => setTab('trajectory')}>
              Trajectory
            </Pill>
          </PillGroup>
        }
      >
        Trajectory
      </PageTitle>

      {tab === 'planner' ? <PlannerTab /> : <TrajectoryTab />}
    </div>
  );
}

// ─── Trajectory tab (unchanged behaviour — quick what-if sandbox) ─────────

function TrajectoryTab() {
  const [target, setTarget] = useState(1000);
  const [years, setYears] = useState(20);
  const [ratePct, setRatePct] = useState(5);
  const [principal, setPrincipal] = useState(0);
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const client = getClient();
    if (!client) return;
    void (async () => {
      if (!cancelled) setError(null);
      const { data, error } = await client.GET('/api/analytics/projections', {
        params: {
          query: {
            target_monthly: target,
            years,
            annual_rate_pct: ratePct,
            starting_principal: principal,
          },
        },
      });
      if (cancelled) return;
      if (error) setError(JSON.stringify(error));
      else setSnap(data ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [target, years, ratePct, principal]);

  const curve = useMemo(() => {
    if (!snap) return [];
    return buildCurve({
      startingPrincipal: Number(snap.starting_principal),
      currentMonthly: Number(snap.current_monthly_savings),
      targetMonthly: Number(snap.target_monthly_savings),
      annualRate: snap.annual_rate,
      years: snap.years,
      steps: Math.max(20, Math.round(snap.years)),
    });
  }, [snap]);

  return (
    <div className="flex flex-col gap-6">
      <Card title="Assumptions">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Slider label="Monthly target" suffix="€" value={target} setValue={setTarget} min={0} max={5000} step={50} />
          <Slider label="Years" value={years} setValue={setYears} min={1} max={50} step={1} />
          <Slider label="Annual return" suffix="%" value={ratePct} setValue={setRatePct} min={-10} max={20} step={0.5} />
          <Slider label="Starting principal" suffix="€" value={principal} setValue={setPrincipal} min={0} max={500000} step={1000} />
        </div>
      </Card>

      {error && (
        <Card>
          <p className="text-sm text-negative">Error: {error}</p>
        </Card>
      )}

      {snap && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card title="Current path">
            <StatBlock
              label="@ horizon"
              value={eur(snap.current_fv)}
              sub={`monthly ${eur(snap.current_monthly_savings)}`}
              tone={Number(snap.current_monthly_savings) >= 0 ? 'positive' : 'negative'}
            />
          </Card>
          <Card title="Target path">
            <StatBlock
              label="@ horizon"
              value={eur(snap.target_fv)}
              sub={`monthly ${eur(snap.target_monthly_savings)}`}
              tone="positive"
            />
          </Card>
          <Card title="Delta">
            <StatBlock
              label="target − current"
              value={eur(snap.delta_fv)}
              sub={`Δ monthly ${eur(Number(snap.target_monthly_savings) - Number(snap.current_monthly_savings))}`}
              tone={Number(snap.delta_fv) > 0 ? 'negative' : 'positive'}
            />
          </Card>
        </div>
      )}

      <Card title={`Future value (${years}y @ ${ratePct.toFixed(1)}%/yr)`}>
        {curve.length === 0 ? (
          <p className="text-sm text-fg-muted py-12 text-center">…</p>
        ) : (
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={curve}>
                <CartesianGrid stroke="var(--stroke-hairline)" strokeDasharray="3 3" />
                <XAxis dataKey="year" tickFormatter={(v) => `${v}y`} tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v) => eur(v)} tick={{ fontSize: 11 }} width={90} />
                <Tooltip
                  formatter={(v: number) => eur(v)}
                  labelFormatter={(label) => `${label} years`}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="current" stroke="#ef4444" strokeWidth={2} dot={false} name="Current" />
                <Line type="monotone" dataKey="target" stroke="#14b8a6" strokeWidth={2} dot={false} name="Target" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Planner tab — server-backed, with income chapters ───────────────────

type LoadState =
  | { kind: 'loading' }
  | { kind: 'empty' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; plans: Plan[]; activeId: string };

function PlannerTab() {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  // Initial fetch.
  useEffect(() => {
    let cancelled = false;
    const client = getClient();
    if (!client) return;
    void (async () => {
      const { data, error } = await client.GET('/api/plans', {});
      if (cancelled) return;
      if (error) {
        setState({ kind: 'error', message: JSON.stringify(error) });
        return;
      }
      const plans = (data ?? []).map(planFromApi);
      if (plans.length === 0) {
        setState({ kind: 'empty' });
        return;
      }
      const def = plans.find((p) => p.isDefault) ?? plans[0];
      setState({ kind: 'ready', plans, activeId: def.id });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.kind === 'loading') {
    return <p className="text-sm text-fg-muted">Loading plans…</p>;
  }
  if (state.kind === 'error') {
    return <p className="text-sm text-negative">Error: {state.message}</p>;
  }
  if (state.kind === 'empty') {
    return <CreateFirstPlan onCreated={(p) => setState({ kind: 'ready', plans: [p], activeId: p.id })} />;
  }

  const ready = state;
  const active = ready.plans.find((p) => p.id === ready.activeId);
  if (!active) return null;

  function update(plans: Plan[], activeId: string) {
    if (plans.length === 0) {
      setState({ kind: 'empty' });
      return;
    }
    setState({ kind: 'ready', plans, activeId });
  }

  function patchActive(next: Plan) {
    update(
      ready.plans.map((p) => (p.id === next.id ? next : p)),
      ready.activeId,
    );
  }

  return (
    <PlannerEditor
      active={active}
      plans={ready.plans}
      onSwitch={(id) => update(ready.plans, id)}
      onLocalPatch={patchActive}
      onListChange={(plans, activeId) => update(plans, activeId)}
    />
  );
}

function CreateFirstPlan({ onCreated }: { onCreated: (p: Plan) => void }) {
  const [name, setName] = useState('My plan');
  const [salary, setSalary] = useState(2500);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function create() {
    const client = getClient();
    if (!client) return;
    setCreating(true);
    setErr(null);
    try {
      const { data, error } = await client.POST('/api/plans', {
        body: {
          name,
          current_age: 30,
          years: 30,
          monthly_investment: 200,
          investment_roi_pct: 6,
          groceries: 300,
          going_out: 100,
          random_monthly: 50,
          yearly_vacations: 1500,
          recurring_expenses: [],
          initial_income: {
            effective_from: isoToday(),
            monthly_salary: salary,
            salary_growth_pct: 2,
            note: null,
          },
          is_default: true,
        },
      });
      if (error || !data) {
        setErr(JSON.stringify(error));
        return;
      }
      onCreated(planFromApi(data));
    } finally {
      setCreating(false);
    }
  }

  return (
    <Card title="Create your first plan" hint="Stored on the server — survives across devices">
      <div className="flex flex-col gap-3 max-w-md">
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-fg-primary">Plan name</span>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-fg-primary">Starting monthly salary</span>
          <Input
            type="number"
            min={0}
            step={50}
            value={salary}
            onChange={(e) => setSalary(Number(e.target.value) || 0)}
          />
        </label>
        <div>
          <Button variant="primary" onClick={create} loading={creating}>
            Create plan
          </Button>
        </div>
        {err && <p className="text-sm text-negative">Error: {err}</p>}
      </div>
    </Card>
  );
}

function PlannerEditor({
  active,
  plans,
  onSwitch,
  onLocalPatch,
  onListChange,
}: {
  active: Plan;
  plans: Plan[];
  onSwitch: (id: string) => void;
  onLocalPatch: (next: Plan) => void;
  onListChange: (plans: Plan[], activeId: string) => void;
}) {
  const combined = useCombinedBalance();

  // Locally apply edits (immediate UX) and debounce-PATCH the server. The
  // last-saved snapshot is the diff baseline so we only send what changed.
  const patchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSaved = useRef<Plan>(active);
  useEffect(() => {
    lastSaved.current = active;
    // Reset the saved baseline whenever the active plan switches.
  }, [active.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function patch(update: Partial<Plan>) {
    const next: Plan = { ...active, ...update };
    onLocalPatch(next);

    if (patchTimer.current) clearTimeout(patchTimer.current);
    patchTimer.current = setTimeout(() => {
      void persistPlanChanges(next, lastSaved.current).then((ok) => {
        if (ok) lastSaved.current = next;
      });
    }, 600);
  }

  // Plan-level operations
  async function newScenario() {
    const client = getClient();
    if (!client) return;
    const { data, error } = await client.POST('/api/plans', {
      body: {
        name: `Scenario ${plans.length + 1}`,
        current_age: active.currentAge,
        years: active.years,
        monthly_investment: 0,
        investment_roi_pct: 6,
        groceries: 0,
        going_out: 0,
        random_monthly: 0,
        yearly_vacations: 0,
        recurring_expenses: [],
        initial_income: {
          effective_from: isoToday(),
          monthly_salary: 2000,
          salary_growth_pct: 2,
          note: null,
        },
        is_default: false,
      },
    });
    if (error || !data) return;
    const fresh = planFromApi(data);
    onListChange([...plans, fresh], fresh.id);
  }

  async function duplicateActive() {
    const client = getClient();
    if (!client) return;
    const { data, error } = await client.POST('/api/plans', {
      body: {
        name: `${active.name} (copy)`,
        current_age: active.currentAge,
        years: active.years,
        monthly_investment: active.monthlyInvestment,
        investment_roi_pct: active.investmentRoiPct,
        groceries: active.groceries,
        going_out: active.goingOut,
        random_monthly: active.randomMonthly,
        yearly_vacations: active.yearlyVacations,
        recurring_expenses: active.recurringExpenses,
        starting_cash_override: active.startingCashOverride,
        starting_holdings_override: active.startingHoldingsOverride,
        initial_income: active.incomes[0]
          ? {
              effective_from: active.incomes[0].effectiveFrom,
              monthly_salary: active.incomes[0].monthlySalary,
              salary_growth_pct: active.incomes[0].salaryGrowthPct,
              note: active.incomes[0].note,
            }
          : {
              effective_from: isoToday(),
              monthly_salary: 0,
              salary_growth_pct: 0,
              note: null,
            },
        is_default: false,
      },
    });
    if (error || !data) return;

    let fresh = planFromApi(data);
    // Duplicate any extra chapters (the create call only seeds the first).
    for (const c of active.incomes.slice(1)) {
      const r = await client.POST('/api/plans/{plan_id}/incomes', {
        params: { path: { plan_id: fresh.id } },
        body: {
          effective_from: c.effectiveFrom,
          monthly_salary: c.monthlySalary,
          salary_growth_pct: c.salaryGrowthPct,
          note: c.note,
        },
      });
      if (r.data) fresh = planFromApi(r.data);
    }
    onListChange([...plans, fresh], fresh.id);
  }

  async function deleteActive() {
    if (!confirm(`Delete "${active.name}"?`)) return;
    const client = getClient();
    if (!client) return;
    await client.DELETE('/api/plans/{plan_id}', {
      params: { path: { plan_id: active.id } },
    });
    const remaining = plans.filter((p) => p.id !== active.id);
    if (remaining.length === 0) {
      onListChange([], '');
      return;
    }
    onListChange(remaining, remaining[0].id);
  }

  async function makeDefault() {
    const client = getClient();
    if (!client) return;
    const { data } = await client.PATCH('/api/plans/{plan_id}', {
      params: { path: { plan_id: active.id } },
      body: {
        is_default: true,
        clear_starting_cash_override: false,
        clear_starting_holdings_override: false,
      },
    });
    if (!data) return;
    const updated = planFromApi(data);
    onListChange(
      plans.map((p) =>
        p.id === updated.id ? updated : p.isDefault ? { ...p, isDefault: false } : p,
      ),
      updated.id,
    );
  }

  // Income chapter operations
  async function addChapter(values: {
    effectiveFrom: string;
    monthlySalary: number;
    salaryGrowthPct: number;
    note: string | null;
  }) {
    const client = getClient();
    if (!client) return null;
    const { data, error } = await client.POST('/api/plans/{plan_id}/incomes', {
      params: { path: { plan_id: active.id } },
      body: {
        effective_from: values.effectiveFrom,
        monthly_salary: values.monthlySalary,
        salary_growth_pct: values.salaryGrowthPct,
        note: values.note,
      },
    });
    if (error || !data) return JSON.stringify(error);
    onLocalPatch(planFromApi(data));
    return null;
  }

  async function patchChapter(chapter: IncomeChapter, fields: Partial<IncomeChapter>) {
    const client = getClient();
    if (!client) return;
    let clearNote = false;
    let note: string | null | undefined = undefined;
    if ('note' in fields) {
      if (fields.note === null) clearNote = true;
      else note = fields.note ?? null;
    }
    const body = {
      ...('effectiveFrom' in fields ? { effective_from: fields.effectiveFrom } : {}),
      ...('monthlySalary' in fields ? { monthly_salary: fields.monthlySalary } : {}),
      ...('salaryGrowthPct' in fields ? { salary_growth_pct: fields.salaryGrowthPct } : {}),
      ...(note !== undefined ? { note } : {}),
      clear_note: clearNote,
    };
    const { data } = await client.PATCH('/api/plans/{plan_id}/incomes/{income_id}', {
      params: { path: { plan_id: active.id, income_id: chapter.id } },
      body,
    });
    if (data) onLocalPatch(planFromApi(data));
  }

  async function deleteChapter(chapter: IncomeChapter) {
    if (active.incomes.length <= 1) {
      alert("Can't delete the only chapter — edit it instead.");
      return;
    }
    if (!confirm(`Delete chapter starting ${chapter.effectiveFrom}?`)) return;
    const client = getClient();
    if (!client) return;
    const { data } = await client.DELETE('/api/plans/{plan_id}/incomes/{income_id}', {
      params: { path: { plan_id: active.id, income_id: chapter.id } },
    });
    if (data) onLocalPatch(planFromApi(data));
  }

  return (
    <div className="flex flex-col gap-6">
      <PlanBar
        plans={plans}
        activeId={active.id}
        onPick={onSwitch}
        onNew={newScenario}
        onDuplicate={duplicateActive}
        onDelete={deleteActive}
        onRename={(name) => patch({ name })}
        onMakeDefault={makeDefault}
        active={active}
      />

      <StartingBalanceCard
        plan={active}
        combined={combined}
        onOverrideCash={(v) => patch({ startingCashOverride: v })}
        onOverrideHoldings={(v) => patch({ startingHoldingsOverride: v })}
      />

      <IncomeChaptersCard
        plan={active}
        onAdd={addChapter}
        onPatch={patchChapter}
        onDelete={deleteChapter}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Investment">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <NumberField
              label="Monthly contribution"
              suffix="€"
              value={active.monthlyInvestment}
              setValue={(v) => patch({ monthlyInvestment: v })}
              min={0}
              step={25}
            />
            <Slider
              label="Annual return"
              suffix="%"
              value={active.investmentRoiPct}
              setValue={(v) => patch({ investmentRoiPct: v })}
              min={-5}
              max={20}
              step={0.1}
            />
          </div>
        </Card>

        <Card title="Monthly spending">
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <NumberField
                label="Food / groceries"
                suffix="€"
                value={active.groceries}
                setValue={(v) => patch({ groceries: v })}
                min={0}
                step={10}
              />
              <NumberField
                label="Going out"
                suffix="€"
                value={active.goingOut}
                setValue={(v) => patch({ goingOut: v })}
                min={0}
                step={10}
              />
              <NumberField
                label="Random items / month"
                suffix="€"
                value={active.randomMonthly}
                setValue={(v) => patch({ randomMonthly: v })}
                min={0}
                step={10}
              />
              <NumberField
                label="Yearly vacations"
                suffix="€"
                value={active.yearlyVacations}
                setValue={(v) => patch({ yearlyVacations: v })}
                min={0}
                step={50}
                hint="amortised across 12 months"
              />
            </div>
            <div className="flex justify-between text-sm border-t border-hairline pt-3">
              <span className="text-fg-muted">Subtotal</span>
              <span className="font-mono tabular-nums">
                {eur(monthlySpendingSubtotal(active))}/mo
              </span>
            </div>
          </div>
        </Card>
      </div>

      <Card title="Recurring monthly expenses" hint="rent, loans, subscriptions, …">
        <RecurringExpenseList
          items={active.recurringExpenses}
          setItems={(items) => patch({ recurringExpenses: items })}
        />
      </Card>

      <Card title="Horizon">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Slider
            label="Years"
            value={active.years}
            setValue={(v) => patch({ years: v })}
            min={1}
            max={50}
            step={1}
          />
          <NumberField
            label="Current age"
            value={active.currentAge}
            setValue={(v) => patch({ currentAge: v })}
            min={0}
            step={1}
            hint="shown alongside years on the projection chart"
          />
        </div>
      </Card>

      <PlannerSummary
        plan={active}
        startingBalance={effectiveStartingBalance(
          active,
          combined.accountsEur,
          combined.holdingsEur,
        )}
      />
    </div>
  );
}

/**
 * PATCH the differing scalar fields on the server. The shape of the body
 * is the union of "fields the user changed" between two snapshots; this
 * keeps us off the wire when only an income chapter changed.
 *
 * Income chapters are NOT diffed here — they have their own dedicated
 * endpoints and are managed via `addChapter` / `patchChapter` /
 * `deleteChapter` in `PlannerEditor`.
 */
async function persistPlanChanges(next: Plan, prev: Plan): Promise<boolean> {
  const client = getClient();
  if (!client) return false;

  type PatchBody = components['schemas']['TrajectoryPlanUpdate'];

  let clearCash = false;
  let cashOverride: number | undefined;
  if (next.startingCashOverride !== prev.startingCashOverride) {
    if (next.startingCashOverride === null) clearCash = true;
    else cashOverride = next.startingCashOverride;
  }
  let clearHoldings = false;
  let holdingsOverride: number | undefined;
  if (next.startingHoldingsOverride !== prev.startingHoldingsOverride) {
    if (next.startingHoldingsOverride === null) clearHoldings = true;
    else holdingsOverride = next.startingHoldingsOverride;
  }

  const recurringChanged = !shallowEqualRecurring(next.recurringExpenses, prev.recurringExpenses);

  const body = {
    ...(next.name !== prev.name ? { name: next.name } : {}),
    ...(next.currentAge !== prev.currentAge ? { current_age: next.currentAge } : {}),
    ...(next.years !== prev.years ? { years: next.years } : {}),
    ...(next.monthlyInvestment !== prev.monthlyInvestment
      ? { monthly_investment: next.monthlyInvestment }
      : {}),
    ...(next.investmentRoiPct !== prev.investmentRoiPct
      ? { investment_roi_pct: next.investmentRoiPct }
      : {}),
    ...(next.groceries !== prev.groceries ? { groceries: next.groceries } : {}),
    ...(next.goingOut !== prev.goingOut ? { going_out: next.goingOut } : {}),
    ...(next.randomMonthly !== prev.randomMonthly ? { random_monthly: next.randomMonthly } : {}),
    ...(next.yearlyVacations !== prev.yearlyVacations
      ? { yearly_vacations: next.yearlyVacations }
      : {}),
    ...(cashOverride !== undefined ? { starting_cash_override: cashOverride } : {}),
    ...(holdingsOverride !== undefined
      ? { starting_holdings_override: holdingsOverride }
      : {}),
    ...(recurringChanged
      ? {
          recurring_expenses: next.recurringExpenses.map((r) => ({
            id: r.id,
            name: r.name,
            price: r.price,
          })),
        }
      : {}),
    clear_starting_cash_override: clearCash,
    clear_starting_holdings_override: clearHoldings,
  } satisfies PatchBody;

  const { error } = await client.PATCH('/api/plans/{plan_id}', {
    params: { path: { plan_id: next.id } },
    body,
  });
  return !error;
}

function shallowEqualRecurring(a: RecurringExpense[], b: RecurringExpense[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].id !== b[i].id || a[i].name !== b[i].name || a[i].price !== b[i].price) return false;
  }
  return true;
}

// ─── Income chapters card ─────────────────────────────────────────────────

function IncomeChaptersCard({
  plan,
  onAdd,
  onPatch,
  onDelete,
}: {
  plan: Plan;
  onAdd: (values: {
    effectiveFrom: string;
    monthlySalary: number;
    salaryGrowthPct: number;
    note: string | null;
  }) => Promise<string | null>;
  onPatch: (chapter: IncomeChapter, fields: Partial<IncomeChapter>) => Promise<void>;
  onDelete: (chapter: IncomeChapter) => Promise<void>;
}) {
  const sorted = useMemo(
    () =>
      [...plan.incomes].sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? -1 : 1)),
    [plan.incomes],
  );
  const [adding, setAdding] = useState(false);
  const today = isoToday();

  return (
    <Card
      title="Income chapters"
      hint="Each fork is a 'chapter' — fork your plan when income changes (raise, job change, time off)."
    >
      <div className="flex flex-col gap-3">
        <ul className="flex flex-col divide-y divide-hairline">
          {sorted.map((c, i) => {
            const isCurrent =
              c.effectiveFrom <= today &&
              (i === sorted.length - 1 || sorted[i + 1].effectiveFrom > today);
            return (
              <ChapterRow
                key={c.id}
                chapter={c}
                isCurrent={isCurrent}
                onPatch={(fields) => onPatch(c, fields)}
                onDelete={() => onDelete(c)}
                canDelete={sorted.length > 1}
              />
            );
          })}
        </ul>
        {adding ? (
          <AddChapterForm
            defaultFrom={maxFrom(sorted, today)}
            onCancel={() => setAdding(false)}
            onSave={async (values) => {
              const err = await onAdd(values);
              if (err === null) setAdding(false);
              return err;
            }}
          />
        ) : (
          <div>
            <Button variant="secondary" size="sm" onClick={() => setAdding(true)}>
              + Fork plan (add chapter)
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

function maxFrom(sorted: IncomeChapter[], today: string): string {
  // Default the new chapter's effective_from to "today, but not before the
  // last chapter" so the form can't accidentally collide with an existing
  // row's date.
  const last = sorted[sorted.length - 1];
  if (!last) return today;
  return last.effectiveFrom > today ? last.effectiveFrom : today;
}

function ChapterRow(props: {
  chapter: IncomeChapter;
  isCurrent: boolean;
  onPatch: (fields: Partial<IncomeChapter>) => Promise<void>;
  onDelete: () => Promise<void>;
  canDelete: boolean;
}) {
  // Each prop snapshot remounts via a server-version-derived `key`, so we
  // can safely use plain useState without the "re-sync prop into state"
  // pattern that triggers React 19's set-state-in-effect lint.
  const c = props.chapter;
  const stableKey = `${c.id}|${c.effectiveFrom}|${c.monthlySalary}|${c.salaryGrowthPct}|${c.note ?? ''}`;
  return <ChapterRowInner key={stableKey} {...props} />;
}

function ChapterRowInner({
  chapter,
  isCurrent,
  onPatch,
  onDelete,
  canDelete,
}: {
  chapter: IncomeChapter;
  isCurrent: boolean;
  onPatch: (fields: Partial<IncomeChapter>) => Promise<void>;
  onDelete: () => Promise<void>;
  canDelete: boolean;
}) {
  // Local mirror so inputs stay editable; commit on blur.
  const [salary, setSalary] = useState(chapter.monthlySalary);
  const [growth, setGrowth] = useState(chapter.salaryGrowthPct);
  const [note, setNote] = useState(chapter.note ?? '');
  const [from, setFrom] = useState(chapter.effectiveFrom);

  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-end sm:gap-3">
      <label className="flex flex-col gap-1 text-xs text-fg-muted">
        Effective from
        <Input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          onBlur={() => from !== chapter.effectiveFrom && void onPatch({ effectiveFrom: from })}
          className="!w-40"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs text-fg-muted">
        Monthly salary
        <Input
          type="number"
          min={0}
          step={50}
          value={salary}
          onChange={(e) => setSalary(Number(e.target.value) || 0)}
          onBlur={() => salary !== chapter.monthlySalary && void onPatch({ monthlySalary: salary })}
          className="!w-32 text-right font-mono"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs text-fg-muted">
        Growth %/yr
        <Input
          type="number"
          step={0.1}
          value={growth}
          onChange={(e) => setGrowth(Number(e.target.value) || 0)}
          onBlur={() => growth !== chapter.salaryGrowthPct && void onPatch({ salaryGrowthPct: growth })}
          className="!w-24 text-right font-mono"
        />
      </label>
      <label className="flex flex-1 flex-col gap-1 text-xs text-fg-muted">
        Note
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => {
            const trimmed = note.trim();
            if (trimmed === (chapter.note ?? '')) return;
            void onPatch({ note: trimmed === '' ? null : trimmed });
          }}
          placeholder="raise to senior, left job, …"
        />
      </label>
      <div className="flex items-center gap-2">
        {isCurrent && (
          <span
            className="rounded-full bg-hero-wash px-2 py-0.5 text-xs"
            style={{ color: 'var(--hero-start)' }}
          >
            current
          </span>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => void onDelete()}
          disabled={!canDelete}
          aria-label="Delete chapter"
          title={canDelete ? 'Delete this chapter' : 'A plan needs at least one chapter'}
        >
          ×
        </Button>
      </div>
    </li>
  );
}

function AddChapterForm({
  defaultFrom,
  onCancel,
  onSave,
}: {
  defaultFrom: string;
  onCancel: () => void;
  onSave: (values: {
    effectiveFrom: string;
    monthlySalary: number;
    salaryGrowthPct: number;
    note: string | null;
  }) => Promise<string | null>;
}) {
  const [from, setFrom] = useState(defaultFrom);
  const [salary, setSalary] = useState(0);
  const [growth, setGrowth] = useState(2);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-3 rounded-md bg-sunken p-3 sm:flex-row sm:items-end">
      <label className="flex flex-col gap-1 text-xs text-fg-muted">
        Effective from
        <Input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className="!w-40"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs text-fg-muted">
        Monthly salary
        <Input
          type="number"
          min={0}
          step={50}
          value={salary}
          onChange={(e) => setSalary(Number(e.target.value) || 0)}
          className="!w-32 text-right font-mono"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs text-fg-muted">
        Growth %/yr
        <Input
          type="number"
          step={0.1}
          value={growth}
          onChange={(e) => setGrowth(Number(e.target.value) || 0)}
          className="!w-24 text-right font-mono"
        />
      </label>
      <label className="flex flex-1 flex-col gap-1 text-xs text-fg-muted">
        Note
        <Input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="raise to senior"
        />
      </label>
      <div className="flex items-center gap-2">
        <Button
          variant="primary"
          size="sm"
          loading={busy}
          onClick={async () => {
            setBusy(true);
            setErr(null);
            const e = await onSave({
              effectiveFrom: from,
              monthlySalary: salary,
              salaryGrowthPct: growth,
              note: note.trim() === '' ? null : note.trim(),
            });
            setBusy(false);
            if (e !== null) setErr(e);
          }}
        >
          Add
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
      {err && <span className="text-xs text-negative">{err}</span>}
    </div>
  );
}

// ─── Plan bar + starting balance + recurring + summary ────────────────────

function PlanBar({
  plans,
  activeId,
  onPick,
  onNew,
  onDuplicate,
  onDelete,
  onRename,
  onMakeDefault,
  active,
}: {
  plans: Plan[];
  activeId: string;
  onPick: (id: string) => void;
  onNew: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onRename: (name: string) => void;
  onMakeDefault: () => void;
  active: Plan;
}) {
  return (
    <Card
      title="Saved plans"
      hint="The default trajectory is the one you'll see first when you open this page."
    >
      <div className="flex flex-wrap items-center gap-3">
        <Select value={activeId} onChange={(e) => onPick(e.target.value)}>
          {plans.map((p) => (
            <option key={p.id} value={p.id}>
              {p.isDefault ? `★ ${p.name}` : p.name}
            </option>
          ))}
        </Select>
        <Input
          type="text"
          value={active.name}
          onChange={(e) => onRename(e.target.value)}
          className="min-w-[12rem] flex-1"
          placeholder="Plan name"
        />
        {active.isDefault ? (
          <span
            className="rounded-full bg-hero-wash px-3 py-1.5 text-sm font-medium"
            style={{ color: 'var(--hero-start)' }}
          >
            ★ Default
          </span>
        ) : (
          <Button variant="secondary" size="sm" onClick={onMakeDefault}>
            Set as default
          </Button>
        )}
        <Button variant="secondary" size="sm" onClick={onNew}>
          + New
        </Button>
        <Button variant="secondary" size="sm" onClick={onDuplicate}>
          Duplicate
        </Button>
        <Button variant="destructive" size="sm" onClick={onDelete}>
          Delete
        </Button>
      </div>
    </Card>
  );
}

function StartingBalanceCard({
  plan,
  combined,
  onOverrideCash,
  onOverrideHoldings,
}: {
  plan: Plan;
  combined: ReturnType<typeof useCombinedBalance>;
  onOverrideCash: (v: number | null) => void;
  onOverrideHoldings: (v: number | null) => void;
}) {
  const cashOverridden = plan.startingCashOverride !== null;
  const holdingsOverridden = plan.startingHoldingsOverride !== null;
  const anyOverride = cashOverridden || holdingsOverridden;
  const effective = effectiveStartingBalance(plan, combined.accountsEur, combined.holdingsEur);

  function toggleOverride(on: boolean) {
    if (on) {
      onOverrideCash(combined.accountsEur);
      onOverrideHoldings(combined.holdingsEur);
    } else {
      onOverrideCash(null);
      onOverrideHoldings(null);
    }
  }

  return (
    <Card title="Starting balance" hint="cash sits idle · holdings compound at the investment rate">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div className="flex flex-col gap-0.5">
            <span className="text-xs uppercase tracking-wide text-fg-muted">
              Combined balance from imports
            </span>
            <span className="text-2xl font-semibold tabular-nums">
              {combined.loading ? '…' : eur(combined.totalEur)}
            </span>
            <span className="text-xs text-fg-muted">
              {combined.loading
                ? 'Loading…'
                : combined.error
                  ? `Could not load: ${combined.error}`
                  : combined.hasData
                    ? `accounts ${eur(combined.accountsEur)} · holdings ${eur(combined.holdingsEur)}` +
                      (combined.capturedAt ? ` · as of ${shortDate(combined.capturedAt)}` : '')
                    : 'No imports yet — connect a bank or upload a CSV.'}
            </span>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={anyOverride}
              onChange={(e) => toggleOverride(e.target.checked)}
              style={{ accentColor: 'var(--hero-start)' }}
            />
            <span>Override</span>
          </label>
        </div>

        {anyOverride && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-hairline pt-3">
            <NumberField
              label="Cash (accounts)"
              suffix="€"
              value={plan.startingCashOverride ?? combined.accountsEur}
              setValue={(v) => onOverrideCash(v)}
              min={0}
              step={100}
            />
            <NumberField
              label="Holdings (invested)"
              suffix="€"
              value={plan.startingHoldingsOverride ?? combined.holdingsEur}
              setValue={(v) => onOverrideHoldings(v)}
              min={0}
              step={100}
            />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm border-t border-hairline pt-3">
          <div className="flex flex-col">
            <span className="text-xs uppercase tracking-wide text-fg-muted">Cash</span>
            <span className="font-mono tabular-nums">
              {eur(effective.cash)}
              {cashOverridden ? <span className="text-xs text-fg-muted"> · override</span> : null}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-xs uppercase tracking-wide text-fg-muted">Invested</span>
            <span className="font-mono tabular-nums">
              {eur(effective.invested)}
              {holdingsOverridden ? <span className="text-xs text-fg-muted"> · override</span> : null}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="text-xs uppercase tracking-wide text-fg-muted">Total</span>
            <span className="font-mono tabular-nums">{eur(effective.cash + effective.invested)}</span>
          </div>
        </div>
      </div>
    </Card>
  );
}

function RecurringExpenseList({
  items,
  setItems,
}: {
  items: RecurringExpense[];
  setItems: (next: RecurringExpense[]) => void;
}) {
  const add = useCallback(() => {
    setItems([...items, { id: cryptoRandomId(), name: '', price: 0 }]);
  }, [items, setItems]);
  function update(id: string, patch: Partial<RecurringExpense>) {
    setItems(items.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  }
  function remove(id: string) {
    setItems(items.filter((s) => s.id !== id));
  }

  const total = recurringExpensesTotal(items);

  return (
    <div className="flex flex-col gap-3">
      {items.length === 0 && (
        <p className="text-sm text-fg-muted">No recurring expenses yet.</p>
      )}
      {items.map((s) => (
        <div key={s.id} className="flex items-center gap-2">
          <Input
            type="text"
            value={s.name}
            onChange={(e) => update(s.id, { name: e.target.value })}
            placeholder="Rent, car loan, Netflix, …"
            className="flex-1"
          />
          <Input
            type="number"
            value={s.price}
            min={0}
            step={0.5}
            onChange={(e) => update(s.id, { price: Number(e.target.value) })}
            className="!w-24 text-right tabular-nums"
          />
          <span className="text-sm text-fg-muted">€/mo</span>
          <Button variant="ghost" size="sm" onClick={() => remove(s.id)} aria-label="Remove expense">
            ×
          </Button>
        </div>
      ))}
      <div className="flex items-center justify-between pt-1">
        <Button variant="secondary" size="sm" onClick={add}>
          + Add expense
        </Button>
        <span className="text-sm text-fg-muted">Total {eur(total)}/mo</span>
      </div>
    </div>
  );
}

function PlannerSummary({
  plan,
  startingBalance,
}: {
  plan: Plan;
  startingBalance: StartingBalance;
}) {
  const points = useMemo(
    () => simulateWithBands(plan, startingBalance),
    [plan, startingBalance],
  );
  const chartData = useMemo(
    () =>
      points.map((p) => ({
        ...p,
        bandSpan: Math.max(0, p.totalHigh - p.totalLow),
      })),
    [points],
  );
  const final = points[points.length - 1];
  const fixed = monthlyFixedExpenses(plan);
  const todaySalary = currentMonthlySalary(plan);
  const initialNet = todaySalary - fixed - plan.monthlyInvestment;
  const volatilityPct = (S_AND_P_ANNUAL_VOLATILITY * 100).toFixed(0);
  const today = isoToday();
  const currentChapter = activeChapterAt(plan.incomes, today);

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card title="Monthly net (today)">
          <StatBlock
            label="salary − spend − invest"
            value={eur(initialNet)}
            sub={
              currentChapter
                ? `salary ${eur(todaySalary)} · spend ${eur(fixed)} · invest ${eur(plan.monthlyInvestment)}`
                : 'no active chapter'
            }
            tone={initialNet >= 0 ? 'positive' : 'negative'}
          />
        </Card>
        <Card title={`Cash @ ${plan.years}y`}>
          <StatBlock
            label={`from ${eur(startingBalance.cash)} start`}
            value={eur(final?.cash ?? 0)}
            tone={(final?.cash ?? 0) >= 0 ? 'positive' : 'negative'}
          />
        </Card>
        <Card title={`Invested @ ${plan.years}y`}>
          <StatBlock
            label={`median @ ${plan.investmentRoiPct.toFixed(1)}%/yr`}
            value={eur(final?.invested ?? 0)}
            sub={
              final
                ? `range ${eur(final.investedLow)} – ${eur(final.investedHigh)}`
                : undefined
            }
            tone="positive"
          />
        </Card>
        <Card title={`Total @ ${plan.years}y`}>
          <StatBlock
            label="median (cash + invested)"
            value={eur(final?.total ?? 0)}
            sub={
              final
                ? `expected ${eur(final.totalMean)} · range ${eur(final.totalLow)} – ${eur(final.totalHigh)}`
                : undefined
            }
            tone={(final?.total ?? 0) >= 0 ? 'positive' : 'negative'}
          />
        </Card>
      </div>

      <Card
        title="Projected wealth"
        hint={`shaded cone = P10–P90 with S&P-like ${volatilityPct}% annual volatility`}
      >
        <div className="h-96">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData}>
              <CartesianGrid stroke="var(--stroke-hairline)" strokeDasharray="3 3" />
              <XAxis
                dataKey="year"
                tickFormatter={(v: number) => `${v}y · age ${plan.currentAge + v}`}
                tick={{ fontSize: 11 }}
                interval="preserveStartEnd"
                minTickGap={40}
              />
              <YAxis tickFormatter={(v) => eur(v)} tick={{ fontSize: 11 }} width={90} />
              <Tooltip
                formatter={(v: number, name: string) => {
                  if (name === 'bandFloor') return [null, null] as unknown as [string, string];
                  return [eur(v), name];
                }}
                labelFormatter={(label: number) =>
                  `${label} ${label === 1 ? 'year' : 'years'} from now · age ${plan.currentAge + label}`
                }
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Area
                type="monotone"
                dataKey="totalLow"
                stackId="band"
                stroke="none"
                fill="transparent"
                fillOpacity={0}
                name="bandFloor"
                legendType="none"
                isAnimationActive={false}
                activeDot={false}
              />
              <Area
                type="monotone"
                dataKey="bandSpan"
                stackId="band"
                stroke="none"
                fill="#14b8a6"
                fillOpacity={0.12}
                name="P10–P90"
                legendType="none"
                isAnimationActive={false}
                activeDot={false}
              />
              <Area
                type="monotone"
                dataKey="cash"
                stackId="wealth"
                stroke="#0ea5e9"
                fill="#0ea5e9"
                fillOpacity={0.35}
                name="Cash"
                isAnimationActive={false}
              />
              <Area
                type="monotone"
                dataKey="invested"
                stackId="wealth"
                stroke="#14b8a6"
                fill="#14b8a6"
                fillOpacity={0.35}
                name="Invested"
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="totalMean"
                stroke="#a855f7"
                strokeWidth={2.5}
                strokeDasharray="6 4"
                dot={false}
                name="Total (expected)"
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}

// ─── Reusable inputs ──────────────────────────────────────────────────────

function Slider({
  label,
  value,
  setValue,
  min,
  max,
  step,
  suffix,
}: {
  label: string;
  value: number;
  setValue: (v: number) => void;
  min: number;
  max: number;
  step: number;
  suffix?: string;
}) {
  return (
    <label className="flex flex-col gap-2 text-sm">
      <div className="flex justify-between">
        <span className="font-medium text-fg-primary">{label}</span>
        <span className="font-mono tabular-nums text-fg-muted">
          {value}
          {suffix}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        style={{ accentColor: 'var(--hero-start)' }}
      />
    </label>
  );
}

function NumberField({
  label,
  value,
  setValue,
  min,
  step,
  suffix,
  hint,
}: {
  label: string;
  value: number;
  setValue: (v: number) => void;
  min?: number;
  step?: number;
  suffix?: string;
  hint?: string;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium text-fg-primary">{label}</span>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          value={value}
          min={min}
          step={step}
          onChange={(e) => setValue(Number(e.target.value))}
          className="flex-1 text-right tabular-nums"
        />
        {suffix && <span className="text-fg-muted">{suffix}</span>}
      </div>
      {hint && <span className="text-xs text-fg-muted">{hint}</span>}
    </label>
  );
}

// --- helpers ----------------------------------------------------------------

function isoToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
